import React, { useEffect, useState } from "react";
import { Check, Copy, Eye, EyeOff, KeyRound, UserRound } from "lucide-react";
import fetchData from "../api/fetchApi";
import { getLang } from "../i18n";

export default function ProfilePage() {
  const isArabic = getLang() === "ar";
  const [openAiKey, setOpenAiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const username = localStorage.getItem("user") || "";
  const role = localStorage.getItem("role") || "client";
  const apiToken = localStorage.getItem("token") || "";

  useEffect(() => {
    fetchData("autoresponse/tokens")
      .then((data) => setOpenAiKey(data?.openai_token || ""))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  }, []);

  const saveKey = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await fetchData("autoresponse/tokens", "PUT", JSON.stringify({ openai_token: openAiKey.trim() }));
      setMessage(isArabic ? "تم حفظ مفتاح OpenAI." : "OpenAI key saved.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async () => {
    if (!apiToken) return;
    await navigator.clipboard.writeText(apiToken);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="dashboard-stack">
      <section className="session-summary">
        <div className="session-identity">
          <span className="session-phone session-phone-online"><UserRound size={25} /></span>
          <div>
            <span className="eyebrow">{isArabic ? "الحساب" : "Account"}</span>
            <h2 className="!font-sans">{username}</h2>
            <span className="status-badge status-badge-online"><span />{role}</span>
          </div>
        </div>
      </section>

      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {message && <div className="notice notice-success" role="status">{message}</div>}

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <article className="panel-card">
          <div className="panel-heading">
            <div><span className="eyebrow">API</span><h3>{isArabic ? "رمز التكامل" : "Integration token"}</h3></div>
            <span className="metric-icon metric-teal"><KeyRound size={20} /></span>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-500">{isArabic ? "استخدم هذا الرمز فقط في خادمك. لا تضعه في كود المتصفح أو مستودع عام." : "Use this token only from your server. Never put it in browser code or a public repository."}</p>
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
            <code className="min-w-0 flex-1 truncate px-2 text-left text-xs text-slate-600" dir="ltr">{apiToken || (isArabic ? "لا يوجد رمز متاح" : "No token available")}</code>
            <button type="button" className="icon-button !h-9 !w-9" onClick={copyToken} disabled={!apiToken} aria-label={isArabic ? "نسخ الرمز" : "Copy token"}>{copied ? <Check size={17} /> : <Copy size={17} />}</button>
          </div>
        </article>

        <article className="panel-card">
          <div className="panel-heading">
            <div><span className="eyebrow">OpenAI</span><h3>{isArabic ? "مفتاح الرد الذكي" : "AI reply key"}</h3></div>
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-500">{isArabic ? "يلزم هذا المفتاح عند تفعيل الردود الذكية. يحفظه الخادم مع إعدادات حسابك." : "This key is required for AI replies and is stored with your server-side account settings."}</p>
          <label className="field-label mt-5 block" htmlFor="openai-key">OpenAI API key</label>
          <div className="password-field mt-2">
            <input id="openai-key" className="field-control font-mono text-xs" type={showKey ? "text" : "password"} value={openAiKey} onChange={(event) => setOpenAiKey(event.target.value)} disabled={loading} autoComplete="off" placeholder="sk-…" dir="ltr" />
            <button type="button" className="password-toggle" onClick={() => setShowKey((visible) => !visible)} aria-label={showKey ? (isArabic ? "إخفاء المفتاح" : "Hide key") : (isArabic ? "إظهار المفتاح" : "Show key")}>{showKey ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          <button type="button" className="primary-button primary-button-auto mt-4" onClick={saveKey} disabled={loading || saving}>{saving ? (isArabic ? "جاري الحفظ…" : "Saving…") : (isArabic ? "حفظ المفتاح" : "Save key")}</button>
        </article>
      </section>
    </div>
  );
}
